import React from "react";
import { useSelector } from "react-redux";

import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { getCurrentDispensary } from "redux/selectors/dispensary";
import { getRetailerInfo } from "utils/getRetailerInfo";
import { getStateOrProvinceLongName } from "utils/getStatesForCountry";
import { retailCopyFormatter } from "utils/retailCopyFormatter";
import slugify from "utils/slugifyURLComponent";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";

const DispensaryBreadcrumbs: React.FC<{
  product?: string;
}> = ({ product }) => {
  const {
    city,
    cityGeocoded,
    deliveryEnabled,
    name,
    pickupEnabled,
    retailType,
    slug,
    state,
  } = useSelector(getCurrentDispensary) || {};
  const countryCode = useDomainCountryCode();
  const isDeliveryOnly = !pickupEnabled && deliveryEnabled;
  const isDispensaryRetailer = retailType === "dispensary";
  const showDeliveryBreadcrumbs = isDispensaryRetailer && isDeliveryOnly;

  const stateLongName = getStateOrProvinceLongName(state);
  const citySlug = slugify(cityGeocoded || city);
  const stateSlug = slugify(stateLongName);

  const baseUrl = useDomain();

  const retailerInfo = getRetailerInfo(countryCode, retailType);
  const { copy, finderUrl, url } = retailerInfo;
  const breadcrumbUrl = showDeliveryBreadcrumbs ? "/delivery" : finderUrl;

  const retailerUrl = `${baseUrl}${url}/${slug}`;
  const retailerBreadcrumb = showDeliveryBreadcrumbs
    ? "Delivery"
    : retailCopyFormatter(copy, "all");

  return (
    <Breadcrumbs>
      <Breadcrumb href={breadcrumbUrl} testId="breadcrumb-retailer">
        {retailerBreadcrumb}
      </Breadcrumb>

      {!!stateSlug && (
        <Breadcrumb
          href={`${breadcrumbUrl}/${stateSlug}`}
          testId="breadcrumb-state"
        >
          {stateLongName}
        </Breadcrumb>
      )}

      {!!citySlug && (
        <Breadcrumb
          href={`${breadcrumbUrl}/${stateSlug}/${citySlug}`}
          testId="breadcrumb-city"
        >
          {cityGeocoded || city}
        </Breadcrumb>
      )}

      {!!name && (
        <Breadcrumb
          href={retailerUrl}
          currentLocation={!product}
          testId="breadcrumb-name"
        >
          {name}
        </Breadcrumb>
      )}

      {!!product && (
        <Breadcrumb currentLocation={true} testId="breadcrumb-product">
          {product}
        </Breadcrumb>
      )}
    </Breadcrumbs>
  );
};

export default DispensaryBreadcrumbs;
